/**
 * SessionPopup - Popup window for session monitoring
 *
 * This page runs in a separate popup window and syncs with the main
 * FloatingWidget via BroadcastChannel. When the session ends, the popup
 * closes and the main window navigates to the session summary.
 */

import { useEffect, useState, useCallback } from 'react';
import { useSessionStore, SyncableSessionState } from '@/state/session-store';
import { AgendaPanel } from '@/components/AgendaPanel';
import { StatusBar } from '@/components/StatusBar';
import { CoachingPanel } from '@/components/coaching';
import {
  useBroadcastChannel,
  setPopupOpen,
  SessionBroadcastMessage,
} from '@/hooks/useBroadcastChannel';

export function SessionPopup() {
  const [sessionEnded, setSessionEnded] = useState(false);
  const [lastSessionId, setLastSessionId] = useState<string | null>(null);

  const {
    status,
    agenda,
    connectionStatus,
    error,
    transcript,
    startedAt,
    syncFromBroadcast,
  } = useSessionStore();

  // Calculate if we should hide the "Preparing" message (fallback after 5 seconds)
  const sessionElapsedMs = startedAt ? Date.now() - startedAt : 0;
  const shouldHidePreparing = sessionElapsedMs > 5000;

  // Handle incoming broadcast messages
  const handleMessage = useCallback((msg: SessionBroadcastMessage) => {
    switch (msg.type) {
      case 'SESSION_START':
        // Session started in main window
        console.log('[Popup] Session started');
        break;

      case 'SESSION_END': {
        // Session ended - close popup and redirect main window
        console.log('[Popup] Session ended, closing popup');
        const payload = msg.payload as { sessionId?: string } | undefined;
        setLastSessionId(payload?.sessionId ?? null);
        setSessionEnded(true);
        break;
      }

      case 'SESSION_RESET':
        // Session reset - close popup
        console.log('[Popup] Session reset, closing popup');
        handleClose();
        break;

      case 'STATE_UPDATE': {
        // Sync state from main window
        const syncState = msg.payload as SyncableSessionState;
        console.log('[Popup] Received state update:', {
          status: syncState?.status,
          connectionStatus: syncState?.connectionStatus,
          transcriptLength: syncState?.recentTranscript?.length,
        });
        if (syncState) {
          syncFromBroadcast(syncState);
        }
        break;
      }

      default:
        break;
    }
  }, [syncFromBroadcast]);

  // Set up broadcast channel
  const { broadcast } = useBroadcastChannel({
    source: 'popup',
    onMessage: handleMessage,
  });

  // Mark popup as open on mount, closed on unmount
  useEffect(() => {
    setPopupOpen(true);

    // Notify main window that popup is ready
    broadcast('POPUP_READY', {});

    // Cleanup on unmount
    return () => {
      setPopupOpen(false);
    };
  }, [broadcast]);

  // Handle session end - close popup and redirect opener
  useEffect(() => {
    if (sessionEnded) {
      // Small delay to ensure main window receives the state
      const timeout = setTimeout(() => {
        // Try to redirect the opener window to session summary
        if (window.opener && !window.opener.closed && lastSessionId) {
          try {
            window.opener.location.href = `/sessions/${lastSessionId}`;
          } catch {
            // Cross-origin error - just close
          }
        }
        // Close this popup
        window.close();
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [sessionEnded, lastSessionId]);

  // Handle close button
  const handleClose = () => {
    setPopupOpen(false);
    window.close();
  };

  // Request session end from main window
  const handleRequestEnd = () => {
    broadcast('POPUP_ACTION', { action: 'end_session' });
  };

  // Handle coaching alerts
  const handleCoachingAlert = useCallback((message: string) => {
    console.log('[Popup] Coaching alert:', message);
  }, []);

  // Session ending screen
  if (sessionEnded) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto mb-4" />
          <p className="text-white">Session complete! Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-white">DemoPath</span>
          {connectionStatus === 'connected' && (
            <span className="w-2 h-2 bg-green-500 rounded-full" title="Connected" />
          )}
          {connectionStatus === 'connecting' && (
            <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" title="Connecting" />
          )}
          {connectionStatus === 'disconnected' && (
            <span className="w-2 h-2 bg-red-500 rounded-full" title="Disconnected" />
          )}
        </div>
        <button
          onClick={handleClose}
          className="text-slate-400 hover:text-white text-sm"
          title="Close popup"
        >
          &times;
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-3 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Idle state - waiting for session */}
      {status === 'idle' && (
        <div className="text-center py-12">
          <p className="text-slate-400 mb-2">Waiting for session to start...</p>
          <p className="text-slate-500 text-sm">Start a session from the main window</p>
        </div>
      )}

      {/* Active session */}
      {status === 'listening' && (
        <div className="space-y-4">
          {/* Session Preparing Indicator - shows during initialization, hides after 5s as fallback */}
          {connectionStatus === 'connecting' && !shouldHidePreparing && (
            <div className="p-4 bg-gradient-to-r from-blue-900/50 to-indigo-900/50 border border-blue-700 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-blue-800 flex items-center justify-center">
                  <span className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                </div>
                <div>
                  <p className="font-medium text-blue-100">Preparing session...</p>
                  <p className="text-sm text-blue-300">Connecting to speech recognition</p>
                </div>
              </div>
            </div>
          )}

          {/* Status bar with timer */}
          <StatusBar />

          {/* Recent transcript - shows what's being said */}
          {transcript.length > 0 && (
            <div className="p-3 bg-slate-800 rounded-lg border border-slate-700">
              <div className="text-xs text-slate-400 mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span>Live Transcript</span>
              </div>
              <div className="text-sm text-slate-200 max-h-24 overflow-y-auto">
                {transcript.slice(-5).map((segment, idx) => (
                  <span key={idx} className={segment.isFinal ? 'text-slate-200' : 'text-slate-400'}>
                    {segment.text}{' '}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Coaching panel */}
          <div className="p-3 bg-slate-800 rounded-lg border border-slate-700">
            <CoachingPanel onAlert={handleCoachingAlert} />
          </div>

          {/* Agenda panel */}
          {agenda && (
            <div className="bg-slate-800 rounded-lg border border-slate-700 p-3">
              <AgendaPanel />
            </div>
          )}

          {/* End session button */}
          <button
            onClick={handleRequestEnd}
            className="w-full py-2.5 px-4 rounded-lg font-medium bg-red-600 hover:bg-red-700 text-white transition-colors"
          >
            End Session
          </button>
        </div>
      )}

      {/* Session complete (fallback if not auto-closed) */}
      {status === 'complete' && (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">&#10003;</div>
          <p className="text-white font-medium mb-2">Session Complete</p>
          <p className="text-slate-400 text-sm mb-4">
            View your results in the main window
          </p>
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
          >
            Close Window
          </button>
        </div>
      )}
    </div>
  );
}
